import { Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NewsUser } from '../../../../../models/news/news-user.model';
import { NewsPredefined } from '../../../../../models/news/news-predefined.model';
import { UserCurrencyType } from '../../../../../enums/users/user-currency-type.enum';
import { ApiNewsService } from '../../../../../services/news/api/api-news.service';
import { AlertsService } from '../../../../../services/alerts.service';
import { LanguagesService } from '../../../../../services/languages/languages.service';
import { ModalsService } from '../../../../../services/modals.service';
import { NewsRewardsModalComponent } from '../../../../news/modals/news-rewards-modal/news-rewards-modal.component';
import { NewsUserAddModalComponent } from './modals/news-user-add-modal/news-user-add-modal.component';
import { NewsUserEditModalComponent } from './modals/news-user-edit-modal/news-user-edit-modal.component';

@Component({
  selector: 'app-user-messages-tab',
  templateUrl: './user-messages-tab.component.html',
  standalone: true,
  imports: [CommonModule, TooltipModule, NewsRewardsModalComponent, NewsUserAddModalComponent, NewsUserEditModalComponent]
})
export class UserMessagesTabComponent {

  @Input() userId: number = 0;
  @Input() userLocale: string = '';

  @ViewChild('addModal') addModal!: NewsUserAddModalComponent;
  @ViewChild('editModal') editModal!: NewsUserEditModalComponent;
  @ViewChild('rewardsModal') rewardsModal!: NewsRewardsModalComponent;

  messages: NewsUser[] = [];
  predefinedList: NewsPredefined[] = [];
  isLoading: boolean = false;
  totalResults: number = 0;
  pageNumber: number = 1;
  readonly resultsPerPage: number = 25;

  get totalPages(): number { return Math.ceil(this.totalResults / this.resultsPerPage); }

  readonly currencyTypes = Object.entries(UserCurrencyType)
    .filter(([, v]) => typeof v === 'number') as [string, number][];

  constructor(
    private apiNewsService: ApiNewsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService,
    private languagesService: LanguagesService
  ) {}

  load(): void {
    this.pageNumber = 1;
    this.apiNewsService.getNewsPredefined(1, 1000).then(
      response => { this.predefinedList = response.results; }
    );
    this.refreshMessages();
  }

  getDisplayTitle(message: NewsUser): string {
    if (message.title) return message.title;
    if (message.newsPredefinedId) {
      const predefined = this.predefinedList.find(p => p.newsPredefinedId === message.newsPredefinedId);
      if (predefined && predefined.translations.length > 0) {
        const locale = (this.userLocale ?? '').toLowerCase();
        if (locale) {
          const localePrefix = locale.split(/[_-]/)[0];
          const match = this.languagesService.languages.find(l => {
            const iso = (l.languageIso ?? '').toLowerCase();
            return iso === locale || iso.split(/[_-]/)[0] === localePrefix;
          });
          if (match) {
            const tr = predefined.translations.find(t => t.languageId === match.languageId);
            if (tr) return tr.title;
          }
        }
        return predefined.translations[0].title;
      }
      if (predefined) return predefined.name;
    }
    return '—';
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  goToPage(page: number): void { this.pageNumber = page; this.refreshMessages(); }
  onClickPaginationPrevious(): void { this.pageNumber--; this.refreshMessages(); }
  onClickPaginationNext(): void { this.pageNumber++; this.refreshMessages(); }

  getCurrencyTypeName(type: number): string { return UserCurrencyType[type] ?? String(type); }

  onClickAdd(): void {
    this.addModal.show(this.userId, this.userLocale, () => this.refreshMessages());
  }

  onClickEdit(message: NewsUser): void {
    this.editModal.show(message, this.userLocale, () => this.refreshMessages());
  }

  onClickRewards(message: NewsUser): void {
    this.rewardsModal.show(message.newsId, message.title, message.rewards, () => {
      this.apiNewsService.getNewsUser(this.userId, this.pageNumber, this.resultsPerPage).then(
        response => {
          this.messages = response.results;
          this.totalResults = response.resultsTotal;
          const fresh = response.results.find(m => m.newsId === message.newsId);
          if (fresh) { this.rewardsModal.rewards = fresh.rewards; }
        }
      );
    });
  }

  onClickDelete(message: NewsUser): void {
    this.modalsService.showConfirmModal(() => {
      this.apiNewsService.deleteNews(message.newsId).then(
        () => {
          this.alertsService.displayAlertSuccess('Message deleted !');
          this.refreshMessages();
        }, error => {
          this.alertsService.displayAlertError('Error deleting message : ' + error);
        }
      );
    });
  }

  private refreshMessages(): void {
    this.isLoading = true;
    this.apiNewsService.getNewsUser(this.userId, this.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.messages = response.results;
        this.totalResults = response.resultsTotal;
        if (this.pageNumber > this.totalPages && this.totalPages > 0) {
          this.pageNumber = 1;
          this.refreshMessages();
        }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading messages : ' + error);
      }
    );
  }
}
